import { KeyRound, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { activerTotp, changerMotDePasse, preparerTotp } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { PageHeader } from '@/components/common';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** Changement du mot de passe d'un compte local (obligatoire après un mot de passe initial remis par l'administrateur). */
export function MotDePasse() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const obligatoire = Boolean((location.state as { obligatoire?: boolean } | null)?.obligatoire);
  const [v, setV] = useState({ ancien: '', nouveau: '', confirmation: '' });
  const [enCours, setEnCours] = useState(false);
  const [totp, setTotp] = useState<{ secret: string; lien: string } | null>(null);
  const [codeTotp, setCodeTotp] = useState('');
  const [totpActive, setTotpActive] = useState(false);
  const [totpEnCours, setTotpEnCours] = useState(false);

  const preparer = async () => {
    setTotpEnCours(true);
    try {
      setTotp(await preparerTotp());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Préparation impossible.');
    } finally {
      setTotpEnCours(false);
    }
  };
  const activer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totp || codeTotp.trim().length < 6) return;
    setTotpEnCours(true);
    try {
      await activerTotp(totp.secret, codeTotp.trim());
      setTotpActive(true);
      setTotp(null);
      setCodeTotp('');
      toast.success('Double authentification activée : le code sera demandé à chaque connexion locale.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Code refusé.');
    } finally {
      setTotpEnCours(false);
    }
  };
  const local = session?.utilisateur.source === 'LOCAL';
  const regles = [
    ['12 caractères au moins', v.nouveau.length >= 12],
    ['une majuscule et une minuscule', /[a-z]/.test(v.nouveau) && /[A-Z]/.test(v.nouveau)],
    ['un chiffre', /\d/.test(v.nouveau)],
    ['un signe (!, #, %, +, -, =, ?, @…)', /[^A-Za-z0-9]/.test(v.nouveau)],
    ['différent de l’ancien', v.nouveau !== '' && v.nouveau !== v.ancien],
    ['confirmation identique', v.nouveau !== '' && v.nouveau === v.confirmation],
  ] as const;
  const valide = regles.every(([, ok]) => ok) && v.ancien.length > 0;

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valide) return;
    setEnCours(true);
    try {
      await changerMotDePasse(v.ancien, v.nouveau);
      toast.success('Mot de passe changé.');
      navigate('/tableau-de-bord', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Changement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <PageHeader titre="Mon mot de passe" sousTitre={local ? 'Compte local FONAREV 360.' : 'Votre mot de passe se gère dans Microsoft 365.'} />
      {obligatoire && (
        <Alert>
          <AlertTitle>Changement obligatoire</AlertTitle>
          <AlertDescription>Le mot de passe remis par l’administrateur est provisoire : choisissez le vôtre avant de continuer.</AlertDescription>
        </Alert>
      )}
      {local ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="size-4" /> Nouveau mot de passe
            </CardTitle>
            <CardDescription>Ne le communiquez à personne, pas même à l’administrateur.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={enregistrer} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="ancien">Mot de passe actuel</Label>
                <Input id="ancien" type="password" autoComplete="current-password" value={v.ancien} onChange={(e) => setV({ ...v, ancien: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nouveau">Nouveau mot de passe</Label>
                <Input id="nouveau" type="password" autoComplete="new-password" value={v.nouveau} onChange={(e) => setV({ ...v, nouveau: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirmation">Confirmation</Label>
                <Input id="confirmation" type="password" autoComplete="new-password" value={v.confirmation} onChange={(e) => setV({ ...v, confirmation: e.target.value })} />
              </div>
              <ul className="grid gap-1 text-xs sm:grid-cols-2">
                {regles.map(([l, ok]) => (
                  <li key={l} className={ok ? 'text-emerald-700' : 'text-muted-foreground'}>
                    {ok ? '✓' : '·'} {l}
                  </li>
                ))}
              </ul>
              <div className="flex justify-end">
                <Button type="submit" disabled={!valide || enCours}>
                  Changer le mot de passe
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}
      {local && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="size-4" /> Double authentification
            </CardTitle>
            <CardDescription>Recommandée pour les comptes locaux de secours : un code à six chiffres de votre application d’authentification (Microsoft Authenticator, Google Authenticator…) sera demandé à chaque connexion locale.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {totpActive ? (
              <p className="text-sm">La double authentification est active sur votre compte local.</p>
            ) : totp ? (
              <form onSubmit={activer} className="space-y-3">
                <p className="text-sm">Dans votre application d’authentification, ajoutez un compte avec la clé ci-dessous (saisie manuelle), ou ouvrez le lien depuis un téléphone où l’application est installée, puis saisissez le code affiché.</p>
                <div className="rounded-md border bg-muted/40 p-3 font-mono text-sm break-all">{totp.secret}</div>
                <a className="text-sm text-primary underline" href={totp.lien}>
                  Ouvrir dans l’application d’authentification
                </a>
                <div className="space-y-1.5">
                  <Label htmlFor="code-totp">Code de l’application</Label>
                  <Input id="code-totp" inputMode="numeric" maxLength={7} placeholder="123456" value={codeTotp} onChange={(e) => setCodeTotp(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={totpEnCours || codeTotp.trim().length < 6}>
                    Activer
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setTotp(null)}>
                    Annuler
                  </Button>
                </div>
              </form>
            ) : (
              <Button type="button" variant="secondary" onClick={preparer} disabled={totpEnCours}>
                Activer la double authentification
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
